// /opt/isp_erp/frontend/src/components/warehouse/LotesEquiposPanel.jsx
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, TextField, Button, ButtonGroup, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, Chip, IconButton, Tooltip, Dialog, DialogTitle,
  DialogContent, DialogActions, Grid, Alert, CircularProgress, TablePagination, FormControl,
  InputLabel, Select, MenuItem, FormControlLabel, Switch, Divider, InputAdornment
} from '@mui/material';
import { Edit, Block, Refresh, Search, ListAlt, PictureAsPdf } from '@mui/icons-material';
import { warehouseAPI } from '../../api/warehouse.api';

const pad = (n) => String(n).padStart(2, '0');
const fechaLocal = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const aInput = (d) => `${fechaLocal(d)}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
const fechaCorta = (s) => (s ? String(s).slice(0, 10).split('-').reverse().join('/') : '');
const recorte = {
  display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', maxWidth: 240
};

const detalleError = (err, porDefecto) => {
  const d = err.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map((x) => x.msg).join('; ');
  return porDefecto;
};

const colorEstado = (estado) => ({
  BODEGA: 'success', EN_TRANSITO: 'warning', INSTALADO: 'info', RETIRO_PENDIENTE: 'error', RMA_BAJA: 'default'
}[estado] || 'default');

const Kpi = ({ titulo, valor, detalle, pequeno }) => (
  <Paper
    variant="outlined"
    sx={{ p: 2, width: '100%', minHeight: 104, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}
  >
    <Typography variant="caption" color="text.secondary">{titulo}</Typography>
    <Typography variant={pequeno ? 'h6' : 'h5'} fontWeight="bold" sx={{ lineHeight: 1.3, wordBreak: 'break-word' }}>
      {valor}
    </Typography>
    <Typography variant="caption" color="text.secondary">{detalle || '\u00A0'}</Typography>
  </Paper>
);

const LotesEquiposPanel = () => {
  const hoy = fechaLocal(new Date());
  const [desde, setDesde] = useState(`${hoy.slice(0, 8)}01`);
  const [hasta, setHasta] = useState(hoy);
  const [texto, setTexto] = useState('');
  const [busqueda, setBusqueda] = useState('');
  const [proveedorFiltro, setProveedorFiltro] = useState('');
  const [incluirAnulados, setIncluirAnulados] = useState(false);
  const [proveedores, setProveedores] = useState([]);
  const [data, setData] = useState({ items: [], total: 0, total_equipos: 0 });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [mensaje, setMensaje] = useState('');
  const [descargando, setDescargando] = useState(false);
  const [detalleSn, setDetalleSn] = useState(false);

  const [verLote, setVerLote] = useState(null);
  const [equiposLote, setEquiposLote] = useState({ items: [], total: 0, anulado: false });
  const [pagEq, setPagEq] = useState(0);
  const [textoEq, setTextoEq] = useState('');
  const [busquedaEq, setBusquedaEq] = useState('');
  const [cargandoEq, setCargandoEq] = useState(false);

  const [editando, setEditando] = useState(null);
  const [form, setForm] = useState({});
  const [fechaOriginal, setFechaOriginal] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [errorDialogo, setErrorDialogo] = useState('');

  const [anulando, setAnulando] = useState(null);
  const [motivo, setMotivo] = useState('');
  const [errorAnular, setErrorAnular] = useState('');

  useEffect(() => {
    warehouseAPI.opcionesProveedores().then((r) => setProveedores(r.data || [])).catch(() => setProveedores([]));
  }, []);

  useEffect(() => {
    const t = setTimeout(() => {
      setBusqueda(texto.trim());
      setPage(0);
    }, 400);
    return () => clearTimeout(t);
  }, [texto]);

  useEffect(() => {
    const t = setTimeout(() => {
      setBusquedaEq(textoEq.trim());
      setPagEq(0);
    }, 400);
    return () => clearTimeout(t);
  }, [textoEq]);

  const cargar = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = { page: page + 1, page_size: rowsPerPage, incluir_anulados: incluirAnulados };
      if (desde) params.fecha_desde = desde;
      if (hasta) params.fecha_hasta = hasta;
      if (busqueda) params.q = busqueda;
      if (proveedorFiltro !== '') params.proveedor_id = proveedorFiltro;
      const r = await warehouseAPI.listarLotesEquipos(params);
      setData(r.data);
    } catch (err) {
      setData({ items: [], total: 0, total_equipos: 0 });
      setError(detalleError(err, 'Error al cargar los lotes'));
    } finally {
      setLoading(false);
    }
  }, [desde, hasta, busqueda, proveedorFiltro, incluirAnulados, page, rowsPerPage]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  useEffect(() => {
    if (!verLote) return;
    let vigente = true;
    setCargandoEq(true);
    warehouseAPI
      .equiposDelLote(verLote.id, { q: busquedaEq || undefined, page: pagEq + 1, page_size: 25 })
      .then((r) => {
        if (vigente) setEquiposLote(r.data);
      })
      .catch(() => {
        if (vigente) setEquiposLote({ items: [], total: 0, anulado: false });
      })
      .finally(() => {
        if (vigente) setCargandoEq(false);
      });
    return () => {
      vigente = false;
    };
  }, [verLote, busquedaEq, pagEq]);

  const avisar = (txt) => {
    setMensaje(txt);
    setTimeout(() => setMensaje(''), 5000);
  };

  const bajarPDF = async (params, nombre) => {
    setDescargando(true);
    try {
      const r = await warehouseAPI.descargarIngresosPDF(params);
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', nombre);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      let texto = 'No se pudo generar el PDF';
      try {
        const json = JSON.parse(await err.response.data.text());
        if (typeof json.detail === 'string') texto = json.detail;
      } catch (e) {
        /* sin detalle del servidor */
      }
      setError(texto);
    } finally {
      setDescargando(false);
    }
  };

  const descargarPDF = () => {
    const params = { tipo: 'equipos', incluir_anulados: incluirAnulados, detalle_sn: detalleSn };
    if (desde) params.fecha_desde = desde;
    if (hasta) params.fecha_hasta = hasta;
    if (busqueda) params.q = busqueda;
    if (proveedorFiltro !== '') params.proveedor_id = proveedorFiltro;
    bajarPDF(params, `ingresos_equipos_${desde || 'inicio'}_${hasta || 'hoy'}.pdf`);
  };

  const rango = (tipo) => {
    setPage(0);
    if (tipo === 'hoy') {
      setDesde(fechaLocal(new Date()));
      setHasta(fechaLocal(new Date()));
    } else if (tipo === 'mes') {
      const h = fechaLocal(new Date());
      setDesde(`${h.slice(0, 8)}01`);
      setHasta(h);
    } else {
      setDesde('');
      setHasta('');
    }
  };

  const cambiar = (setter) => (e) => {
    setter(e.target.value);
    setPage(0);
  };

  const abrirVer = (lote) => {
    setTextoEq('');
    setBusquedaEq('');
    setPagEq(0);
    setEquiposLote({ items: [], total: 0, anulado: false });
    setVerLote(lote);
  };

  const abrirEditar = (lote) => {
    const fecha = aInput(new Date(lote.fecha_ingreso));
    setEditando(lote);
    setFechaOriginal(fecha);
    setForm({
      proveedor_id: lote.proveedor_id || '',
      documento_compra: lote.documento_compra || '',
      fecha_compra: lote.fecha_compra ? String(lote.fecha_compra).slice(0, 10) : '',
      descripcion: lote.descripcion || '',
      fecha
    });
    setErrorDialogo('');
  };

  const guardar = async () => {
    setGuardando(true);
    setErrorDialogo('');
    try {
      const cambios = {
        proveedor_id: form.proveedor_id === '' ? null : form.proveedor_id,
        documento_compra: form.documento_compra,
        fecha_compra: form.fecha_compra,
        descripcion: form.descripcion
      };
      if (form.fecha !== fechaOriginal) cambios.fecha_ingreso = form.fecha;
      await warehouseAPI.editarLoteEquipos(editando.id, cambios);
      setEditando(null);
      avisar('Lote actualizado. Los cambios se aplicaron a todos sus equipos.');
      cargar();
    } catch (err) {
      setErrorDialogo(detalleError(err, 'No se pudo actualizar el lote'));
    } finally {
      setGuardando(false);
    }
  };

  const confirmarAnular = async () => {
    if (motivo.trim().length < 3) {
      setErrorAnular('Indica el motivo de la anulación');
      return;
    }
    try {
      await warehouseAPI.anularLoteEquipos(anulando.id, { motivo: motivo.trim() });
      setAnulando(null);
      setMotivo('');
      avisar('Lote anulado. Sus equipos fueron eliminados de bodega.');
      cargar();
    } catch (err) {
      setErrorAnular(detalleError(err, 'No se pudo anular el lote'));
    }
  };

  return (
    <Card>
      <CardContent>
        <Box sx={{ mb: 2 }}>
          <Typography variant="h6">Lotes de equipos</Typography>
          <Typography variant="body2" color="text.secondary">
            Cada registro o importación de equipos queda como un lote con su proveedor, factura y fecha.
          </Typography>
        </Box>

        <Grid container spacing={2} sx={{ mb: 2 }} alignItems="stretch">
          <Grid item xs={12} sm={4} sx={{ display: 'flex' }}>
            <Kpi titulo="Lotes en el período" valor={data.total} detalle="Registros que cumplen el filtro" />
          </Grid>
          <Grid item xs={12} sm={4} sx={{ display: 'flex' }}>
            <Kpi titulo="Equipos ingresados" valor={data.total_equipos || 0} detalle="Suma de los lotes vigentes" />
          </Grid>
          <Grid item xs={12} sm={4} sx={{ display: 'flex' }}>
            <Kpi
              titulo="Período"
              valor={`${fechaCorta(desde) || 'Inicio'} – ${fechaCorta(hasta) || 'Hoy'}`}
              detalle="Hora de Ecuador"
              pequeno
            />
          </Grid>
        </Grid>

        <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: '#fafafa' }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={6} md={2.5}>
              <TextField fullWidth type="date" size="small" label="Desde" value={desde} onChange={cambiar(setDesde)} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={6} md={2.5}>
              <TextField fullWidth type="date" size="small" label="Hasta" value={hasta} onChange={cambiar(setHasta)} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} md={4}>
              <ButtonGroup size="small" variant="outlined">
                <Button onClick={() => rango('hoy')}>Hoy</Button>
                <Button onClick={() => rango('mes')}>Este mes</Button>
                <Button onClick={() => rango('todo')}>Todo</Button>
              </ButtonGroup>
            </Grid>
            <Grid item xs={12} md={3}>
              <Button fullWidth size="small" variant="outlined" startIcon={<Refresh />} onClick={cargar}>
                Actualizar
              </Button>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Proveedor</InputLabel>
                <Select label="Proveedor" value={proveedorFiltro} onChange={cambiar(setProveedorFiltro)}>
                  <MenuItem value="">Todos</MenuItem>
                  {proveedores.map((p) => (
                    <MenuItem key={p.id} value={p.id}>{p.nombre}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={5}>
              <TextField
                fullWidth
                size="small"
                label="Buscar factura, archivo, descripción o usuario"
                value={texto}
                onChange={(e) => setTexto(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search fontSize="small" />
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControlLabel
                control={<Switch checked={incluirAnulados} onChange={(e) => { setIncluirAnulados(e.target.checked); setPage(0); }} />}
                label="Incluir anulados"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={<Switch checked={detalleSn} onChange={(e) => setDetalleSn(e.target.checked)} />}
                label="Incluir la lista de SN de cada lote en el PDF"
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <Button
                fullWidth
                size="small"
                variant="contained"
                startIcon={descargando ? <CircularProgress size={16} /> : <PictureAsPdf />}
                onClick={descargarPDF}
                disabled={descargando}
              >
                Descargar PDF
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {mensaje && <Alert severity="success" sx={{ mb: 2 }}>{mensaje}</Alert>}
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

        <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 600 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ bgcolor: '#f5f5f5' }}>Fecha y registro</TableCell>
                <TableCell sx={{ bgcolor: '#f5f5f5' }}>Proveedor y factura</TableCell>
                <TableCell sx={{ bgcolor: '#f5f5f5' }}>Equipos</TableCell>
                <TableCell sx={{ bgcolor: '#f5f5f5' }}>Origen</TableCell>
                <TableCell sx={{ bgcolor: '#f5f5f5' }}>Descripción</TableCell>
                <TableCell align="center" sx={{ bgcolor: '#f5f5f5' }}>Estado y acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center"><CircularProgress size={24} /></TableCell>
                </TableRow>
              ) : data.items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">No hay lotes en este período</TableCell>
                </TableRow>
              ) : (
                data.items.map((l) => {
                  const f = new Date(l.fecha_ingreso);
                  return (
                    <TableRow key={l.id} hover sx={{ opacity: l.anulado ? 0.55 : 1 }}>
                      <TableCell sx={{ minWidth: 150 }}>
                        <Typography variant="body2" fontWeight="bold">{f.toLocaleDateString('es-EC')}</Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          {f.toLocaleTimeString('es-EC', { hour: '2-digit', minute: '2-digit' })} · {l.usuario || '—'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block">Lote N.º {l.id}</Typography>
                      </TableCell>
                      <TableCell sx={{ minWidth: 180 }}>
                        <Typography variant="body2">{l.proveedor || 'Sin proveedor'}</Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Factura: {l.documento_compra || '—'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Compra: {fechaCorta(l.fecha_compra) || '—'}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ minWidth: 150 }}>
                        <Typography variant="body2" fontWeight="bold">{l.cantidad} equipos</Typography>
                        {l.anulado ? (
                          <Typography variant="caption" color="text.secondary" display="block">Eliminados al anular</Typography>
                        ) : (
                          <Typography variant="caption" color="text.secondary" display="block">
                            En bodega: {l.en_bodega} · Fuera: {l.fuera_de_bodega}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip size="small" variant="outlined" label={l.origen === 'EXCEL' ? 'Excel' : 'Manual'} />
                        {l.archivo_nombre && (
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {l.archivo_nombre}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Tooltip title={l.descripcion || ''} disableHoverListener={!l.descripcion}>
                          <Typography variant="body2" sx={recorte}>{l.descripcion || '—'}</Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="center" sx={{ whiteSpace: 'nowrap' }}>
                        {l.anulado ? (
                          <>
                            <Tooltip title={`Anulado por ${l.anulado_por || '—'}: ${l.motivo_anulacion || ''}`}>
                              <Chip size="small" label="Anulado" sx={{ mr: 0.5 }} />
                            </Tooltip>
                            <Tooltip title="Ver SN del lote">
                              <IconButton size="small" onClick={() => abrirVer(l)}><ListAlt fontSize="small" /></IconButton>
                            </Tooltip>
                          </>
                        ) : (
                          <>
                            <Chip size="small" color="success" label="Vigente" sx={{ mr: 0.5 }} />
                            <Tooltip title="Ver equipos del lote">
                              <IconButton size="small" onClick={() => abrirVer(l)}><ListAlt fontSize="small" /></IconButton>
                            </Tooltip>
                            <Tooltip title="Editar lote">
                              <IconButton size="small" onClick={() => abrirEditar(l)}><Edit fontSize="small" /></IconButton>
                            </Tooltip>
                            <Tooltip title={l.puede_anularse ? 'Anular lote' : 'No se puede anular: hay equipos fuera de bodega'}>
                              <span>
                                <IconButton
                                  size="small"
                                  color="error"
                                  disabled={!l.puede_anularse}
                                  onClick={() => { setAnulando(l); setMotivo(''); setErrorAnular(''); }}
                                >
                                  <Block fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={data.total}
          page={page}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[10, 25, 50, 100]}
          labelRowsPerPage="Filas por página:"
          labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
          onPageChange={(e, nueva) => setPage(nueva)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      </CardContent>

      <Dialog open={!!verLote} onClose={() => setVerLote(null)} maxWidth="md" fullWidth>
        <DialogTitle>Equipos del lote N.º {verLote?.id}</DialogTitle>
        <DialogContent dividers>
          <TextField
            size="small"
            label="Buscar SN o modelo"
            value={textoEq}
            onChange={(e) => setTextoEq(e.target.value)}
            sx={{ mb: 2, minWidth: 280 }}
          />
          <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 420 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ bgcolor: '#f5f5f5' }}>SN</TableCell>
                  <TableCell sx={{ bgcolor: '#f5f5f5' }}>Modelo</TableCell>
                  <TableCell sx={{ bgcolor: '#f5f5f5' }}>Tipo</TableCell>
                  <TableCell sx={{ bgcolor: '#f5f5f5' }}>Estado</TableCell>
                  <TableCell sx={{ bgcolor: '#f5f5f5' }}>Poseedor</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {cargandoEq ? (
                  <TableRow><TableCell colSpan={5} align="center"><CircularProgress size={22} /></TableCell></TableRow>
                ) : equiposLote.items.length === 0 ? (
                  <TableRow><TableCell colSpan={5} align="center">Sin equipos</TableCell></TableRow>
                ) : (
                  equiposLote.items.map((e) => (
                    <TableRow key={e.id || e.sn} hover>
                      <TableCell><strong>{e.sn}</strong></TableCell>
                      <TableCell>{e.modelo || '—'}</TableCell>
                      <TableCell>{e.tipo || '—'}</TableCell>
                      <TableCell><Chip size="small" color={colorEstado(e.estado_actual)} label={e.estado_actual} /></TableCell>
                      <TableCell>{e.poseedor || '—'}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={equiposLote.total || 0}
            page={pagEq}
            rowsPerPage={25}
            rowsPerPageOptions={[25]}
            labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
            onPageChange={(e, nueva) => setPagEq(nueva)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVerLote(null)}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!editando} onClose={() => !guardando && setEditando(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Editar lote N.º {editando?.id}</DialogTitle>
        <DialogContent dividers>
          {errorDialogo && <Alert severity="error" sx={{ mb: 2 }}>{errorDialogo}</Alert>}
          <Typography variant="subtitle2" color="primary" gutterBottom>Compra</Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel>Proveedor</InputLabel>
                <Select label="Proveedor" value={form.proveedor_id ?? ''} onChange={(e) => setForm({ ...form, proveedor_id: e.target.value })}>
                  <MenuItem value="">Sin proveedor</MenuItem>
                  {proveedores.map((p) => (
                    <MenuItem key={p.id} value={p.id}>{p.nombre}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" label="Factura / documento" value={form.documento_compra || ''} onChange={(e) => setForm({ ...form, documento_compra: e.target.value })} />
            </Grid>
            <Grid item xs={6}>
              <TextField fullWidth size="small" type="date" label="Fecha de compra" value={form.fecha_compra || ''} onChange={(e) => setForm({ ...form, fecha_compra: e.target.value })} InputLabelProps={{ shrink: true }} />
            </Grid>
          </Grid>
          <Typography variant="subtitle2" color="primary" gutterBottom>Registro</Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                size="small"
                type="datetime-local"
                label="Fecha y hora del ingreso"
                value={form.fecha || ''}
                onChange={(e) => setForm({ ...form, fecha: e.target.value })}
                InputLabelProps={{ shrink: true }}
                inputProps={{ max: aInput(new Date()) }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" multiline rows={2} label="Descripción" value={form.descripcion || ''} onChange={(e) => setForm({ ...form, descripcion: e.target.value })} />
            </Grid>
          </Grid>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
            El proveedor, la factura y la fecha de compra se actualizan también en todos los equipos del lote.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditando(null)} disabled={guardando}>Cancelar</Button>
          <Button variant="contained" onClick={guardar} disabled={guardando}>
            {guardando ? 'Guardando...' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!anulando} onClose={() => setAnulando(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Anular lote N.º {anulando?.id}</DialogTitle>
        <DialogContent>
          {errorAnular && <Alert severity="error" sx={{ mb: 2 }}>{errorAnular}</Alert>}
          <Alert severity="warning" sx={{ mb: 2 }}>
            Se eliminarán de bodega los <strong>{anulando?.cantidad} equipos</strong> de este lote. El lote queda en el
            historial como anulado, con la lista de sus SN.
          </Alert>
          <TextField fullWidth multiline rows={2} label="Motivo de la anulación *" value={motivo} onChange={(e) => setMotivo(e.target.value)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAnulando(null)}>Cancelar</Button>
          <Button color="error" variant="contained" onClick={confirmarAnular}>Anular lote</Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default LotesEquiposPanel;
